'use client'

import 'src/lib/helpers/augment-zod'
import { P, match } from 'ts-pattern'
import { api } from 'src/trpc/react'
import { createContext, useContext, useEffect, useState } from 'react'
import { enableMapSet } from 'immer'
import { getQueryKey } from '@trpc/react-query' // This import is a side effect that affects all zod parse/safeParse methods
import { useAuth } from './auth'
import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import type { PropsWithChildren } from 'react'

interface LoadingProperties {
  state: boolean
  text: string
}

interface PROVIDER_VALUES {
  loading: LoadingProperties
  isHydrated: boolean
  skipLoader: boolean
  setSkipLoader: (skipLoader: boolean) => void
}

const DEFAULT_FETCH_TEXT = 'Retrieving data...'
const DEFAULT_MUTATION_TEXT = 'Saving changes...'

const LoadingContext = createContext({})

// THIS IS A SIDE EFFECT THAT AFFECTS ALL IMMER INSTANCES (used in zustand stores).
enableMapSet()

const refineResultsMutationKey = getQueryKey(api.search.refine)
const updateSearchQueryMutationKey = getQueryKey(api.search.searchQuery.update)
const createSearchQueryMutationKey = getQueryKey(api.search.searchQuery.create)
const clearFiltersMutationKey = getQueryKey(api.filters.clear)
const clearResultsMutationKey = getQueryKey(api.search.clearResults)

const getSearchQueryKey = getQueryKey(api.search.get)
const getSearchQueryQueryKey = getQueryKey(api.search.searchQuery.get)

export const LoadingProvider = ({ children }: PropsWithChildren) => {
  const [isHydrated, setIsHydrated] = useState(false)
  const [skipLoader, setSkipLoader] = useState(false)
  const { userLoading } = useAuth()

  const isMutatingRefineResults =
    useIsMutating({
      mutationKey: refineResultsMutationKey,
    }) > 0

  const isMutatingUpdateSearchQuery =
    useIsMutating({
      mutationKey: updateSearchQueryMutationKey,
    }) > 0

  const isMutatingCreateSearchQuery =
    useIsMutating({
      mutationKey: createSearchQueryMutationKey,
    }) > 0

  const isMutatingClearFilters =
    useIsMutating({
      mutationKey: clearFiltersMutationKey,
    }) > 0

  const isMutatingClearResults =
    useIsMutating({
      mutationKey: clearResultsMutationKey,
    }) > 0

  const isFetchingSearch =
    useIsFetching({
      queryKey: getSearchQueryKey,
      predicate: (query) => {
        return query.state.isInvalidated
      },
    }) > 0

  const isFetchingSearchQuery =
    useIsFetching({
      queryKey: getSearchQueryQueryKey,
      predicate: (query) => {
        return query.state.isInvalidated
      },
    }) > 0

  const loadingMessage = match(true)
    .returnType<string>()
    .with(
      P.when(() => isMutatingRefineResults),
      () => 'Refining your search query...'
    )
    .with(
      P.when(() => isMutatingUpdateSearchQuery),
      () => 'Updating your search query...'
    )
    .with(
      P.when(() => isMutatingCreateSearchQuery),
      () => 'Creating a new search...'
    )
    .with(
      P.when(() => isMutatingClearFilters || isMutatingClearResults),
      () => DEFAULT_MUTATION_TEXT
    )
    .with(
      P.when(() => isFetchingSearch || isFetchingSearchQuery || userLoading),
      () => DEFAULT_FETCH_TEXT
    )
    .otherwise(() => DEFAULT_FETCH_TEXT)

  const isLoading =
    isMutatingRefineResults ||
    isMutatingUpdateSearchQuery ||
    isMutatingCreateSearchQuery ||
    isMutatingClearFilters ||
    isMutatingClearResults ||
    isFetchingSearch ||
    isFetchingSearchQuery ||
    userLoading

  const providerValues: PROVIDER_VALUES = {
    loading: {
      state: isLoading,
      text: loadingMessage,
    },
    isHydrated,
    skipLoader,
    setSkipLoader,
  }

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  return (
    <LoadingContext.Provider value={providerValues}>
      {children}
    </LoadingContext.Provider>
  )
}

export const useLoading = () => useContext(LoadingContext) as PROVIDER_VALUES
